import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, Play, Download, Trash2, Info, TrendingUp, Target, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { WLLSHonestBacktestService, type WLLSHonestBacktestConfig, type WLLSHonestBacktestResult, type StoredWLLSHonestBacktestSimulation } from '@/services/WLLSHonestBacktestService';
import { supabase } from '@/lib/supabase';

interface WLLSHonestBacktestTabProps {
  fileId: string;
}

export const WLLSHonestBacktestTab = ({ fileId }: WLLSHonestBacktestTabProps) => {
  const [isRunning, setIsRunning] = useState(false);
  const [config, setConfig] = useState<Partial<WLLSHonestBacktestConfig>>({
    startPosition: 50,
    patternRefreshFrequency: 10,
    winThreshold: 0.52,
    directionThreshold: 0.55,
    minSequenceLength: 3,
    maxSequenceLength: 10,
    backtestMode: 'side_aware'
  });
  const [lastResult, setLastResult] = useState<WLLSHonestBacktestResult | null>(null);
  const [selectedSimulation, setSelectedSimulation] = useState<string | null>(null);

  // Fetch file info
  const { data: fileData } = useQuery({
    queryKey: ['wlls-file', fileId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wlls_files')
        .select('filename, total_trades, analysis_status')
        .eq('id', fileId)
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch simulation history
  const { data: simulations, refetch: refetchSimulations } = useQuery({
    queryKey: ['wlls-honest-simulations', fileId],
    queryFn: async () => {
      const result = await WLLSHonestBacktestService.getSimulations(fileId);
      return result.success ? result.data : [];
    }
  });

  // Fetch simulation detail
  const { data: simulationDetail } = useQuery({
    queryKey: ['wlls-honest-simulation-detail', selectedSimulation],
    queryFn: async () => {
      if (!selectedSimulation) return null;
      const result = await WLLSHonestBacktestService.getSimulationDetail(selectedSimulation);
      return result.success ? result.data : null;
    },
    enabled: !!selectedSimulation
  });

  const handleRunBacktest = async () => {
    const validation = WLLSHonestBacktestService.validateConfig({
      ...config,
      fileId
    } as WLLSHonestBacktestConfig);

    if (!validation.isValid) {
      toast.error(`Configuration error: ${validation.errors.join(', ')}`);
      return;
    }

    setIsRunning(true);
    try {
      const result = await WLLSHonestBacktestService.runBacktest({
        ...config,
        fileId
      } as WLLSHonestBacktestConfig);

      if (result.success && result.result) {
        setLastResult(result.result);
        toast.success('WLLS Honest Progressive Backtest completed successfully!');
        refetchSimulations();
      } else {
        toast.error(`Backtest failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Backtest error:', error);
      toast.error('Failed to run backtest');
    } finally {
      setIsRunning(false);
    }
  };

  const handleDeleteSimulation = async (simulationId: string) => {
    try {
      const result = await WLLSHonestBacktestService.deleteSimulation(simulationId);
      if (result.success) {
        toast.success('Simulation deleted successfully');
        refetchSimulations();
        if (selectedSimulation === simulationId) {
          setSelectedSimulation(null);
        }
      } else {
        toast.error(`Failed to delete simulation: ${result.error}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete simulation');
    }
  };

  const exportResults = (result: WLLSHonestBacktestResult) => {
    const csv = WLLSHonestBacktestService.exportToCSV(result);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wlls-honest-backtest-${result.simulationId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Results exported to CSV');
  };

  const getAccuracyBadge = (accuracy: number) => {
    if (accuracy >= 70) return <Badge variant="default" className="bg-green-500">Excellent: {accuracy.toFixed(1)}%</Badge>;
    if (accuracy >= 60) return <Badge variant="secondary">Good: {accuracy.toFixed(1)}%</Badge>;
    if (accuracy >= 50) return <Badge variant="outline">Fair: {accuracy.toFixed(1)}%</Badge>;
    return <Badge variant="destructive">Poor: {accuracy.toFixed(1)}%</Badge>;
  };

  const displayResult = lastResult || (simulationDetail ? {
    config: {
      fileId: simulationDetail.file_id,
      startPosition: simulationDetail.start_position,
      endPosition: simulationDetail.end_position,
      patternRefreshFrequency: simulationDetail.pattern_refresh_frequency,
      winThreshold: simulationDetail.win_threshold,
      directionThreshold: simulationDetail.direction_threshold || 0.55,
      minSequenceLength: simulationDetail.min_sequence_length,
      maxSequenceLength: simulationDetail.max_sequence_length,
      backtestMode: simulationDetail.backtest_mode
    },
    positions: simulationDetail.positions_data,
    summary: {
      totalPositions: simulationDetail.total_positions,
      predictedPositions: simulationDetail.predicted_positions,
      skippedPositions: simulationDetail.skipped_positions,
      correctPredictions: simulationDetail.correct_predictions,
      accuracy: simulationDetail.accuracy,
      directionalAccuracy: simulationDetail.summary_stats.directionalAccuracy || 0,
      skipRate: simulationDetail.skip_rate,
      finalPatternCount: simulationDetail.summary_stats.finalPatternCount,
      initialPatternCount: simulationDetail.summary_stats.initialPatternCount,
      averagePatternsPerPosition: simulationDetail.summary_stats.averagePatternsPerPosition,
      maxPatterns: simulationDetail.summary_stats.maxPatterns,
      minPatterns: simulationDetail.summary_stats.minPatterns
    },
    patternEvolution: simulationDetail.pattern_evolution,
    processingTime: simulationDetail.processing_time_ms,
    simulationId: simulationDetail.id
  } as WLLSHonestBacktestResult : null);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Target className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold">WLLS Honest Progressive Backtest</h2>
          <p className="text-muted-foreground">
            Validate WLLS patterns with progressive learning and side-aware analysis
          </p>
        </div>
      </div>

      {/* File Info */}
      {fileData && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">File Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <Label className="text-muted-foreground">Filename</Label>
                <p className="font-medium">{fileData.filename}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Total Trades</Label>
                <p className="font-medium">{fileData.total_trades}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Status</Label>
                <Badge variant={fileData.analysis_status === 'completed' ? 'default' : 'secondary'}>
                  {fileData.analysis_status}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="configure" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="configure">Configure & Run</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="configure" className="space-y-6">
          {/* Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Backtest Configuration</CardTitle>
              <CardDescription>
                Configure honest progressive backtest parameters for WLLS analysis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startPosition">Start Position</Label>
                  <Input
                    id="startPosition"
                    type="number"
                    min="10"
                    value={config.startPosition || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, startPosition: parseInt(e.target.value) }))}
                    placeholder="50"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Position to start testing (training data: 0 to start-1)</p>
                </div>

                <div>
                  <Label htmlFor="endPosition">End Position (Optional)</Label>
                  <Input
                    id="endPosition"
                    type="number"
                    value={config.endPosition || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, endPosition: e.target.value ? parseInt(e.target.value) : undefined }))}
                    placeholder="Auto (end of data)"
                  />
                </div>

                <div>
                  <Label htmlFor="winThreshold">Win Threshold</Label>
                  <Input
                    id="winThreshold"
                    type="number"
                    min="0.5"
                    max="1"
                    step="0.01"
                    value={config.winThreshold || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, winThreshold: parseFloat(e.target.value) }))}
                    placeholder="0.52"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum win probability to make prediction</p>
                </div>

                <div>
                  <Label htmlFor="directionThreshold">Direction Threshold</Label>
                  <Input
                    id="directionThreshold"
                    type="number"
                    min="0.5"
                    max="1"
                    step="0.01"
                    value={config.directionThreshold || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, directionThreshold: parseFloat(e.target.value) }))}
                    placeholder="0.55"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum consensus to suggest direction (side-aware mode)</p>
                </div>

                <div>
                  <Label htmlFor="backtestMode">Backtest Mode</Label>
                  <Select 
                    value={config.backtestMode} 
                    onValueChange={(value: 'binary' | 'side_aware') => 
                      setConfig(prev => ({ ...prev, backtestMode: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="binary">Binary (Win/Loss only)</SelectItem>
                      <SelectItem value="side_aware">Side-aware (Long/Short + Win/Loss)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="patternRefreshFrequency">Pattern Refresh Frequency</Label>
                  <Input
                    id="patternRefreshFrequency"
                    type="number"
                    min="1"
                    value={config.patternRefreshFrequency || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, patternRefreshFrequency: parseInt(e.target.value) }))}
                    placeholder="10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="minSequenceLength">Min Sequence Length</Label>
                  <Input
                    id="minSequenceLength"
                    type="number"
                    min="1"
                    value={config.minSequenceLength || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, minSequenceLength: parseInt(e.target.value) }))}
                    placeholder="3"
                  />
                </div>

                <div>
                  <Label htmlFor="maxSequenceLength">Max Sequence Length</Label>
                  <Input
                    id="maxSequenceLength"
                    type="number"
                    min="1"
                    value={config.maxSequenceLength || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, maxSequenceLength: parseInt(e.target.value) }))}
                    placeholder="10"
                  />
                </div>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Honest Backtest:</strong> Patterns are built only from historical data (positions 0 to start-1). 
                  Each prediction is made before seeing the actual result, then patterns are updated progressively.
                  {config.backtestMode === 'side_aware' && (
                    <span className="block mt-1">
                      <strong>Side-aware mode:</strong> Provides directional bias analysis (Long vs Short) in addition to Win/Loss predictions.
                    </span>
                  )}
                </AlertDescription>
              </Alert>

              <Button 
                onClick={handleRunBacktest} 
                disabled={isRunning}
                className="w-full"
                size="lg"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Running Backtest...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Run WLLS Honest Backtest
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results">
          {displayResult ? (
            <div className="space-y-6">
              {/* Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Backtest Summary</CardTitle>
                  <CardDescription>
                    Mode: {displayResult.config.backtestMode === 'binary' ? 'Binary (Win/Loss)' : 'Side-aware (Long/Short + Win/Loss)'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{displayResult.summary.totalPositions}</div>
                      <div className="text-sm text-muted-foreground">Total Positions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{displayResult.summary.predictedPositions}</div>
                      <div className="text-sm text-muted-foreground">Predicted</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{displayResult.summary.correctPredictions}</div>
                      <div className="text-sm text-muted-foreground">Correct</div>
                    </div>
                    <div className="text-center">
                      {getAccuracyBadge(displayResult.summary.accuracy)}
                      <div className="text-sm text-muted-foreground mt-1">Accuracy</div>
                    </div>
                  </div>

                  {displayResult.config.backtestMode === 'side_aware' && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center">
                          <div className="text-xl font-bold">{displayResult.summary.directionalAccuracy.toFixed(1)}%</div>
                          <div className="text-sm text-muted-foreground">Direction Hint Quality</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold">{displayResult.summary.skipRate.toFixed(1)}%</div>
                          <div className="text-sm text-muted-foreground">Skip Rate</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Final Patterns:</span>
                      <span className="ml-2 font-medium">{displayResult.summary.finalPatternCount}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Avg Patterns/Position:</span>
                      <span className="ml-2 font-medium">{displayResult.summary.averagePatternsPerPosition.toFixed(1)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Processing Time:</span>
                      <span className="ml-2 font-medium">{displayResult.processingTime}ms</span>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <Button onClick={() => exportResults(displayResult)} size="sm" variant="outline">
                      <Download className="mr-2 h-4 w-4" />
                      Export CSV
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Position Results */}
              <Card>
                <CardHeader>
                  <CardTitle>Position Results</CardTitle>
                  <CardDescription>
                    Detailed prediction results for each position
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="max-h-96 overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Position</TableHead>
                          <TableHead>Prediction</TableHead>
                          <TableHead>Actual</TableHead>
                          <TableHead>Win Prob</TableHead>
                          {displayResult.config.backtestMode === 'side_aware' && (
                            <TableHead>Dir. Prob (L/S)</TableHead>
                          )}
                          <TableHead>Confidence</TableHead>
                          <TableHead>Result</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayResult.positions.slice(0, 50).map((pos, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{pos.position}</TableCell>
                            <TableCell>
                              <Badge variant={
                                pos.prediction === 'WIN' ? 'default' : 
                                pos.prediction === 'LOSS' ? 'destructive' : 'secondary'
                              }>
                                {pos.prediction}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className={`font-mono ${
                                pos.actualResult.startsWith('1') ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {pos.actualResult}
                              </span>
                            </TableCell>
                            <TableCell>
                              {pos.winProbability ? `${pos.winProbability.toFixed(1)}%` : 'N/A'}
                            </TableCell>
                            {displayResult.config.backtestMode === 'side_aware' && (
                              <TableCell>
                                {pos.longProbability && pos.shortProbability ? 
                                  `${pos.longProbability.toFixed(0)}/${pos.shortProbability.toFixed(0)}` : 'N/A'}
                              </TableCell>
                            )}
                            <TableCell>{pos.confidence.toFixed(1)}</TableCell>
                            <TableCell>
                              {pos.prediction === 'SKIP' ? (
                                <Badge variant="secondary">SKIP</Badge>
                              ) : ((pos.prediction === 'WIN' && pos.actualResult.startsWith('1')) || (pos.prediction === 'LOSS' && pos.actualResult.startsWith('0'))) ? (
                                <Badge variant="default" className="bg-green-500">✓</Badge>
                              ) : (
                                <Badge variant="destructive">✗</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {displayResult.positions.length > 50 && (
                      <p className="text-sm text-muted-foreground text-center mt-2">
                        Showing first 50 of {displayResult.positions.length} positions. Export CSV for full results.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="text-center">
                  <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No Results Yet</h3>
                  <p className="text-muted-foreground">Run a backtest to see results here</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Simulation History</CardTitle>
              <CardDescription>
                Previous WLLS honest backtest simulations for this file
              </CardDescription>
            </CardHeader>
            <CardContent>
              {simulations && simulations.length > 0 ? (
                <div className="space-y-4">
                  {simulations.map((sim) => (
                    <div 
                      key={sim.id} 
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedSimulation === sim.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                      }`}
                      onClick={() => setSelectedSimulation(sim.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant={sim.backtest_mode === 'binary' ? 'outline' : 'default'}>
                              {sim.backtest_mode}
                            </Badge>
                            {getAccuracyBadge(sim.accuracy)}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {sim.predicted_positions}/{sim.total_positions} positions, 
                            {sim.correct_predictions} correct, 
                            {sim.skip_rate.toFixed(1)}% skip rate
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Created: {new Date(sim.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSimulation(sim.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No Simulations Yet</h3>
                  <p className="text-muted-foreground">Run your first honest backtest to see history</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};